import React, { useState, useEffect } from 'react';
import { message, Progress, Card, Row, Col } from 'antd';
import { Form } from 'react-bootstrap';
import { Input } from 'antd';
import { SaveOutlined, HeartOutlined } from '@ant-design/icons';
import { oneKlicks } from '../../SDK/oneclick';
import { OneKlicksFileSDK } from '../../SDK/OneKlicksFileSDK';
import { getuserdata } from '../../services/apiService';
import ImageShow from '../../Uitiles/ImageShow';

const { TextArea } = Input;

const BloodBankProfileSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [userId, setUserId] = useState(null);

    const [formData, setFormData] = useState({
        display_name: '',
        email: '',
        phone: '',
        address: '',
        description: '',
        photourl: '',
        website: '',
        working_hours: ''
    });

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        try {
            setLoading(true);
            const id = await getuserdata('id');
            setUserId(id);

            const response = await oneKlicks.read('', 'blood_bank', { id: id });

            if (response && response.data && response.data[0]) {
                const profile = response.data[0];
                setFormData({
                    display_name: profile.display_name || '',
                    email: profile.email || '',
                    phone: profile.phone || '',
                    address: profile.address || '',
                    description: profile.description || '',
                    photourl: profile.photourl || profile.profile_photo_url || '',
                    website: profile.website || '',
                    working_hours: profile.working_hours || ''
                });
            }
        } catch (error) {
            console.error('Load profile error:', error);
            message.error('Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);

            const updateData = {
                display_name: formData.display_name,
                phone: formData.phone,
                address: formData.address,
                description: formData.description,
                photourl: formData.photourl,
                profile_photo_url: formData.photourl,
                website: formData.website,
                working_hours: formData.working_hours
            };

            const response = await oneKlicks.update('', 'blood_bank', updateData, { id: userId });
            if (response.success) {
                message.success('Profile updated successfully');
            } else {
                message.error(response.message || 'Failed to update');
            }
        } catch (error) {
            console.error('Save error:', error);
            message.error('Failed to save profile');
        } finally {
            setSaving(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setUploading(true);
            const response = await OneKlicksFileSDK.uploadFile('blood_bank', 'public', file);
            if (response && response.fileId) {
                setFormData({ ...formData, photourl: response.fileId });
                message.success('Image uploaded successfully');
            } else {
                message.error('Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            message.error('Error uploading file');
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <div className="subbody" style={{ width: '100%', padding: '2% 4%' }}>
                <div className="text-center py-5">
                    <Progress percent={100} status="active" showInfo={false} style={{ width: '200px' }} />
                    <div className="mt-2">Loading profile...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="subbody" style={{ width: '100%', padding: '2% 4%' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-1 fw-bold">
                        <HeartOutlined className="me-2 text-danger" />
                        Profile Settings
                    </h4>
                    <p className="text-muted mb-0">Manage your blood bank profile information</p>
                </div>
                <button
                    className="btn btn-primary btn-lg"
                    onClick={handleSave}
                    disabled={saving}
                >
                    <SaveOutlined className="me-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>

            <Row gutter={[24, 24]}>
                <Col xs={24} md={8}>
                    <Card title="Profile Photo" className="shadow-sm border-0 text-center">
                        <div className="mb-3">
                            <ImageShow
                                image={formData.photourl}
                                width={150}
                                height={150}
                                style={{ borderRadius: '50%', objectFit: 'cover' }}
                            />
                        </div>
                        <input
                            type="file"
                            className="form-control"
                            onChange={handleFileUpload}
                            accept="image/*"
                            disabled={uploading}
                        />
                        {uploading && (
                            <Progress percent={100} status="active" showInfo={false} className="mt-2" />
                        )}
                    </Card>
                </Col>

                <Col xs={24} md={16}>
                    <Card title="Basic Information" className="shadow-sm border-0">
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Blood Bank Name</Form.Label>
                                    <Input
                                        value={formData.display_name}
                                        onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                                        placeholder="Enter blood bank name"
                                        size="large"
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Email</Form.Label>
                                    <Input
                                        value={formData.email}
                                        disabled
                                        placeholder="Email"
                                        size="large"
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Phone</Form.Label>
                                    <Input
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        placeholder="Enter phone number"
                                        size="large"
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Website</Form.Label>
                                    <Input
                                        value={formData.website}
                                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                        placeholder="Enter website URL"
                                        size="large"
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={24}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Address</Form.Label>
                                    <TextArea
                                        rows={2}
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        placeholder="Enter full address"
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={24}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Working Hours</Form.Label>
                                    <Input
                                        value={formData.working_hours}
                                        onChange={(e) => setFormData({ ...formData, working_hours: e.target.value })}
                                        placeholder="e.g., Mon-Sat: 9AM - 6PM"
                                        size="large"
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={24}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">Description</Form.Label>
                                    <TextArea
                                        rows={4}
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Enter description about your blood bank"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default BloodBankProfileSettings;
